<template>
  <b-modal
    modal-class="component-modal-customer"
    dialog-class="cm-dialog"
    ref="CustomerModal"
    size="xl fluid"
    :hide-footer="!isManagerOrCounter || readOnly"
    no-close-on-backdrop
    @hidden="close"
  >
    <template #modal-header="{ close }">
      <h5 class="modal-title">{{ $t('customer.modalTitle') }}</h5>
      <span class="cm-dn ml-2" data-testid="patient-dn">DN {{ customer.dn || $t('customer.dnAutoAssign') }}</span>
      <span
        v-if="allergyAlert"
        class="cm-allergy ml-2 text-truncate"
        :title="allergyAlert"
        data-testid="patient-allergy-alert"
      >
        <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
        <b>{{ $t('customer.alert.allergy') }}</b> {{ allergyAlert }}
      </span>
      <div class="cm-lang ml-auto" role="group" aria-label="Language">
        <button
          v-for="lang in ['th', 'en']"
          :key="lang"
          type="button"
          :class="{ on: currentLang === lang }"
          :aria-pressed="String(currentLang === lang)"
          @click="switchLanguage(lang)"
        >{{ lang.toUpperCase() }}</button>
      </div>
      <button
        type="button"
        aria-label="Close"
        class="cm-close"
        @click="close()"
      ><i class="fas fa-times" aria-hidden="true"></i></button>
    </template>
    <b-form
      ref="newPatientForm"
      id="newPatientForm"
      autocomplete="off"
      @submit.prevent="debouncedSavePatient"
    >
      <div class="cm-cols">
        <!-- 1: who the patient is, and who to call -->
        <div class="cm-col">
          <fieldset class="cm-card" :disabled="!isManagerOrCounter || readOnly">
            <div class="cm-head">
              <span class="cm-tile cm-tile-blue"><i class="fas fa-user" aria-hidden="true"></i></span>
              <div class="flex-grow-1" style="min-width: 0">
                <h6 class="cm-card-title">{{ $t('customer.section.personal') }}</h6>
                <div class="cm-card-hint">{{ $t('customer.sectionHint.personal') }}</div>
              </div>
              <div class="cm-reader">
                <SmartCard
                  ref="smartcard"
                  @getReaderData="getReaderData"
                  @showDialogToast="showDialogToast"
                >
                </SmartCard>
                <SmartCardMOI
                  ref="smartcardMoi"
                  @getReaderData="getReaderData"
                  @showDialogToast="showDialogToast"
                >
                </SmartCardMOI>
              </div>
            </div>
            <div class="d-flex align-items-center" style="gap: 14px">
              <b-form-group class="mb-0 flex-shrink-0 cm-photo" :disabled="!isManagerOrCounter">
                <upload-avatar-form
                  ref="UploadAvatar"
                  square
                  size="84px"
                  :uploadMaxSize="180"
                  :default-id="defaultContentId"
                  @callback="uploadAvatarCallback"
                >
                  <template #actions="{ hasImage, pick, webcam, remove }">
                    <b-dropdown
                      class="cm-photo-menu"
                      variant="link"
                      toggle-class="cm-photo-badge"
                      no-caret
                      data-testid="patient-photo-menu"
                    >
                      <template #button-content>
                        <i class="fas fa-camera" aria-hidden="true"></i>
                        <span class="sr-only">{{ $t('customer.photoMenu.title') }}</span>
                      </template>
                      <b-dropdown-item-button @click="pick">
                        <i class="fas fa-upload fa-fw mr-2" aria-hidden="true"></i>{{ $t('customer.photoMenu.upload') }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button @click="webcam">
                        <i class="fas fa-camera fa-fw mr-2" aria-hidden="true"></i>{{ $t('customer.photoMenu.camera') }}
                      </b-dropdown-item-button>
                      <b-dropdown-item-button v-if="hasImage" variant="danger" @click="remove">
                        <i class="fas fa-trash-alt fa-fw mr-2" aria-hidden="true"></i>{{ $t('customer.photoMenu.remove') }}
                      </b-dropdown-item-button>
                    </b-dropdown>
                  </template>
                </upload-avatar-form>
              </b-form-group>
              <div class="flex-grow-1 d-flex flex-column" style="gap: 8px; min-width: 0">
                <label class="cm-field">
                  <span class="cm-label">{{ $t('customer.idType') }}</span>
                  <select class="form-control form-control-sm" v-model="customer.personalType">
                    <option v-for="item in personalTypeList" :key="item.val" :value="item.val">
                      {{ item.label }}
                    </option>
                  </select>
                </label>
                <label class="cm-field">
                  <span class="cm-label">{{ $t('customer.idCard') }}</span>
                  <b-form-input
                    size="sm"
                    id="personalId"
                    :class="{ 'is-invalid': $v.customer.personalId.$error }"
                    v-model="$v.customer.personalId.$model"
                    trim
                    type="text"
                    @change="debouncedCheckDuplicates"
                  ></b-form-input>
                  <small v-if="$v.customer.personalId.$error" class="text-danger">
                    {{ $t('customer.validation.invalid13Digits') }}
                  </small>
                </label>
              </div>
            </div>
            <b-popover
              id="matched-pid-popover"
              target="personalId"
              triggers="manual"
              variant="danger"
            >
              <template #title>
                <b-button
                  @click="closePopover"
                  class="close"
                  aria-label="Close"
                >
                  <span
                    class="float-right ml-2"
                    style="line-height: 0.8"
                    aria-hidden="true"
                    >&times;</span
                  > </b-button
                >{{ $t('customer.validation.duplicateId') }}
              </template>
              <b-button
                block
                variant="danger"
                @click="openSearchCustomerModal(customer.personalId)"
                >{{ $t('customer.selectExistingPatient') }}</b-button
              >
            </b-popover>

            <div class="cm-grid" style="grid-template-columns: 5rem minmax(0, 1fr) minmax(0, 1fr)">
              <div class="cm-field">
                <span class="cm-label">{{ $t('customer.titleTh') }}</span>
                <vue-typeahead-bootstrap
                  input-class="form-control-sm"
                  v-model="customer.titleNameTh"
                  :data="getTitleNameList"
                  :serializer="s => s.label"
                  ref="titleNameTh"
                  :minMatchingChars="0"
                  :showOnFocus="true"
                />
              </div>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.firstNameTh') }} <span class="text-danger">*</span></span>
                <b-form-input
                  size="sm"
                  id="firstname-input"
                  class="cm-req"
                  data-testid="patient-firstname"
                  autofocus
                  v-model="$v.customer.firstNameTh.$model"
                  trim
                  lazy
                  type="text"
                  :class="{ 'is-invalid': $v.customer.firstNameTh.$error }"
                  @change="debouncedCheckDuplicates"
                ></b-form-input>
              </label>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.lastNameTh') }} <span class="text-danger">*</span></span>
                <b-form-input
                  size="sm"
                  id="lastname-input"
                  class="cm-req"
                  data-testid="patient-lastname"
                  v-model="$v.customer.lastNameTh.$model"
                  trim
                  lazy
                  type="text"
                  :class="{ 'is-invalid': $v.customer.lastNameTh.$error }"
                  @change="debouncedCheckDuplicates"
                ></b-form-input>
              </label>
            </div>
            <b-popover
              id="matched-name-popover"
              target="lastname-input"
              triggers="manual"
              placement="bottom"
              variant="warning"
            >
              <template #title>
                <b-button
                  @click="closePopover"
                  class="close"
                  aria-label="Close"
                >
                  <span
                    class="float-right ml-2"
                    style="line-height: 0.8"
                    aria-hidden="true"
                    >&times;</span
                  > </b-button
                >{{ $t('customer.validation.duplicateName') }}
              </template>
              <b-button
                block
                variant="warning"
                @click="
                  openSearchCustomerModal(
                    customer.firstNameTh + ' ' + customer.lastNameTh
                  )
                "
                >{{ $t('customer.selectExistingPatient') }}</b-button
              >
            </b-popover>

            <div class="cm-grid" style="grid-template-columns: 5rem minmax(0, 1fr) minmax(0, 1fr)">
              <div class="cm-field">
                <span class="cm-label">{{ $t('customer.titleEn') }}</span>
                <vue-typeahead-bootstrap
                  input-class="form-control-sm"
                  v-model="customer.titleNameEn"
                  :data="getTitleNameEngList"
                  :serializer="s => s.label"
                  ref="titleNameEng"
                  :minMatchingChars="0"
                  :showOnFocus="true"
                />
              </div>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.firstNameEn') }}</span>
                <b-form-input size="sm" v-model="customer.firstNameEn" type="text"></b-form-input>
              </label>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.lastNameEn') }}</span>
                <b-form-input size="sm" v-model="customer.lastNameEn" type="text"></b-form-input>
              </label>
            </div>

            <div class="cm-grid" style="grid-template-columns: repeat(3, minmax(0, 1fr))">
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.nickname') }}</span>
                <b-form-input size="sm" v-model="customer.nickName" type="text"></b-form-input>
              </label>
              <div class="cm-field">
                <span class="cm-label">{{ $t('customer.gender') }}</span>
                <vue-typeahead-bootstrap
                  input-class="form-control-sm"
                  v-model="customer.gender"
                  :data="getGenderList"
                  :serializer="s => s.label"
                  ref="gender"
                  :min-matching-chars="0"
                  :showOnFocus="true"
                />
              </div>
              <label v-if="isManagerOrCounter" class="cm-field">
                <span class="cm-label">{{ $t('customer.nationality') }}</span>
                <b-form-input size="sm" v-model="customer.nationality" type="text" maxlength="20"></b-form-input>
              </label>
            </div>

            <div class="cm-grid" style="grid-template-columns: minmax(0, 1fr) 7.5rem">
              <div class="cm-field" data-testid="patient-dob">
                <span class="cm-label">{{ $t('customer.dob') }}</span>
                <datetime
                  input-class="form-control"
                  v-model="rawDob"
                  format="cccc dd LLLL yyyy"
                  :max-datetime="now"
                  value-zone="UTC+7"
                  auto
                  :flow="['year', 'month', 'date']"
                >
                </datetime>
              </div>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.dnOld') }}</span>
                <b-form-input size="sm" type="text" trim v-model="customer.existDn" />
              </label>
            </div>
          </fieldset>

          <fieldset
            class="cm-card cm-grow"
            data-testid="patient-emergency"
            :disabled="!isManagerOrCounter || readOnly"
          >
            <div class="cm-head">
              <span class="cm-tile cm-tile-orange"><i class="fas fa-user-shield" aria-hidden="true"></i></span>
              <h6 class="cm-card-title">{{ $t('customer.section.emergency') }}</h6>
            </div>
            <div class="cm-grid" style="grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr)">
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.emergencyName') }}</span>
                <b-form-input size="sm" v-model="customer.emergencyContactName" type="text" maxlength="100"></b-form-input>
              </label>
              <div class="cm-field">
                <span class="cm-label">{{ $t('customer.emergencyRelationship') }}</span>
                <vue-typeahead-bootstrap
                  input-class="form-control-sm"
                  v-model="customer.emergencyContactRelationship"
                  :data="translatedRelationshipOptions"
                  :minMatchingChars="0"
                  :showOnFocus="true"
                  maxlength="100"
                />
              </div>
              <div class="cm-field" style="grid-column: span 2">
                <span class="cm-label">{{ $t('customer.emergencyPhone') }}</span>
                <div class="d-flex">
                  <CountryCodeSelect v-model="emergencyPhoneCountry" @select="onEmergencyCountryChange" />
                  <b-form-input
                    size="sm"
                    class="flex-grow-1"
                    type="tel"
                    :aria-label="$t('customer.emergencyPhone')"
                    :maxlength="phoneMaxLength(emergencyPhoneCountry)"
                    @input="applyEmergencyPhone"
                    :value="dashedEmergencyPhone"
                  ></b-form-input>
                </div>
              </div>
            </div>
          </fieldset>
        </div>

        <!-- 2: health, contact, coverage -->
        <div class="cm-col">
          <fieldset class="cm-card" :disabled="!isManagerOrCounter || readOnly">
            <div class="cm-head">
              <span class="cm-tile cm-tile-red"><i class="fas fa-heartbeat" aria-hidden="true"></i></span>
              <div>
                <h6 class="cm-card-title">{{ $t('customer.section.health') }}</h6>
                <div class="cm-card-hint">{{ $t('customer.sectionHint.health') }}</div>
              </div>
            </div>
            <medical-checklist
              testid="patient-congenital-disease"
              v-model="$v.customer.congenitalDisease.$model"
              :title="$t('customer.congenitalDisease')"
              :items="diseaseItems"
              required
              tone="warning"
              :invalid="$v.customer.congenitalDisease.$error"
            />
            <medical-checklist
              testid="patient-medication-history"
              v-model="$v.customer.medicationHistory.$model"
              :title="$t('customer.medicationHistory')"
              :groups="medicationGroups"
              required
              :invalid="$v.customer.medicationHistory.$error"
            />
            <medical-checklist
              testid="patient-allergic"
              v-model="$v.customer.allergic.$model"
              :title="$t('customer.allergies')"
              :items="allergyItems"
              required
              tone="danger"
              :invalid="$v.customer.allergic.$error"
            />
          </fieldset>

          <fieldset v-if="isManagerOrCounter" class="cm-card" :disabled="readOnly">
            <div class="cm-head">
              <span class="cm-tile cm-tile-green"><i class="fas fa-phone-alt" aria-hidden="true"></i></span>
              <h6 class="cm-card-title">{{ $t('customer.section.contact') }}</h6>
            </div>
            <div class="cm-grid" style="grid-template-columns: repeat(2, minmax(0, 1fr))">
              <div class="cm-field" style="grid-column: span 2">
                <span class="cm-label">{{ $t('customer.mobilePlaceholder') }} <span class="text-danger">*</span></span>
                <div class="d-flex">
                  <CountryCodeSelect v-model="mobileCountry" @select="onMobileCountryChange" />
                  <b-form-input
                    size="sm"
                    class="flex-grow-1 cm-req"
                    data-testid="patient-mobile"
                    type="text"
                    :aria-label="$t('customer.mobilePlaceholder')"
                    :class="{ 'is-invalid': $v.customer.mobile.$error }"
                    :maxlength="phoneMaxLength(mobileCountry)"
                    @input="applyMobileNumber"
                    :value="dashedMobile"
                  ></b-form-input>
                </div>
                <small v-if="$v.customer.mobile.$error && !$v.customer.mobile.validPhone" class="text-danger">
                  {{ phoneLengthHint(mobileCountry, 'mobile') }}
                </small>
              </div>
              <div class="cm-field" style="grid-column: span 2">
                <span class="cm-label">{{ $t('customer.homePlaceholder') }}</span>
                <div class="d-flex">
                  <CountryCodeSelect v-model="telCountry" @select="onTelCountryChange" />
                  <b-form-input
                    size="sm"
                    class="flex-grow-1"
                    type="text"
                    :aria-label="$t('customer.homePlaceholder')"
                    :class="{ 'is-invalid': $v.customer.tel.$error }"
                    :maxlength="phoneMaxLength(telCountry)"
                    @input="applyTelNumber"
                    :value="dashedTel"
                  ></b-form-input>
                </div>
                <small v-if="$v.customer.tel.$error && !$v.customer.tel.validPhone" class="text-danger">
                  {{ phoneLengthHint(telCountry, 'home') }}
                </small>
              </div>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.lineId') }}</span>
                <b-form-input size="sm" v-model="customer.lineId" type="text"></b-form-input>
              </label>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.email') }}</span>
                <b-form-input size="sm" v-model="customer.email" type="email"></b-form-input>
              </label>
            </div>
          </fieldset>


          <fieldset class="cm-card cm-grow" :disabled="!isManagerOrCounter || readOnly">
            <div class="cm-head">
              <span class="cm-tile cm-tile-teal"><i class="fas fa-shield-alt" aria-hidden="true"></i></span>
              <h6 class="cm-card-title">{{ $t('customer.section.coverage') }}</h6>
            </div>
            <div class="cm-grid" style="grid-template-columns: repeat(2, minmax(0, 1fr))">
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.medicalInsurance') }}</span>
                <select class="form-control form-control-sm" v-model="customer.medicalInsuranceType">
                  <option :value="null" selected disabled>{{ $t('customer.medicalInsurance') }}</option>
                  <option value="government_employee">{{ $t('customer.insurance.government_employee') }}</option>
                  <option value="self_pay">{{ $t('customer.insurance.self_pay') }}</option>
                  <option value="social_security">{{ $t('customer.insurance.social_security') }}</option>
                  <option value="state_enterprise">{{ $t('customer.insurance.state_enterprise') }}</option>
                  <option value="universal_coverage">{{ $t('customer.insurance.universal_coverage') }}</option>
                  <option value="other">{{ $t('customer.insurance.other') }}</option>
                </select>
              </label>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.attendingDoctor') }}</span>
                <select class="form-control form-control-sm" v-model="customer.doctorId">
                  <option value="null" selected disabled>{{ $t('customer.attendingDoctor') }}</option>
                  <option v-for="val in doctorList" :key="val.uid" :value="val.uid">
                    {{ val.fullName }}
                  </option>
                </select>
              </label>
            </div>
          </fieldset>
        </div>

        <!-- 3: address, referral, rarely used -->
        <div class="cm-col">
          <fieldset v-if="isManagerOrCounter" class="cm-card" :disabled="readOnly">
            <div class="cm-head">
              <span class="cm-tile cm-tile-violet"><i class="fas fa-map-marker-alt" aria-hidden="true"></i></span>
              <h6 class="cm-card-title">{{ $t('customer.section.address') }}</h6>
            </div>
            <label class="cm-field">
              <span class="cm-label">{{ $t('customer.address') }}</span>
              <b-form-textarea size="sm" v-model="customer.address" rows="2" max-rows="4"></b-form-textarea>
            </label>
            <div class="cm-grid" style="grid-template-columns: repeat(3, minmax(0, 1fr))">
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.subDistrict') }}</span>
                <b-form-input size="sm" v-model="customer.subDistrict" type="text"></b-form-input>
              </label>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.district') }}</span>
                <b-form-input size="sm" v-model="customer.district" type="text"></b-form-input>
              </label>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.province') }}</span>
                <b-form-input size="sm" v-model="customer.province" type="text"></b-form-input>
              </label>
              <label class="cm-field">
                <span class="cm-label">{{ $t('customer.postcode') }}</span>
                <b-form-input size="sm" v-model="customer.postcode" type="text"></b-form-input>
              </label>
              <label class="cm-field" style="grid-column: span 2">
                <span class="cm-label">{{ $t('customer.country') }}</span>
                <b-form-input size="sm" v-model="customer.country" type="text"></b-form-input>
              </label>
            </div>
          </fieldset>


          <fieldset class="cm-card cm-grow" data-testid="patient-referral" :disabled="!isManagerOrCounter || readOnly">
            <div class="cm-head">
              <span class="cm-tile cm-tile-pink"><i class="fas fa-bullhorn" aria-hidden="true"></i></span>
              <h6 class="cm-card-title">{{ $t('customer.referralSource') }}</h6>
            </div>
            <b-form-checkbox-group
              v-model="customer.referralSources"
              :options="referralChipOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
              class="cm-chips"
              :aria-label="$t('customer.referralSource')"
            ></b-form-checkbox-group>
          </fieldset>

          <!-- not a disabled fieldset itself: read-only viewers must still be able to open it -->
          <div class="cm-card cm-more">
            <button
              type="button"
              class="cm-more-toggle"
              :aria-expanded="String(showMore)"
              data-testid="patient-more-toggle"
              @click="showMore = !showMore"
            >
              <span class="cm-tile cm-tile-gray"><i class="fas fa-ellipsis-h" aria-hidden="true"></i></span>
              <span class="flex-grow-1" style="min-width: 0">
                <span class="cm-card-title d-block">{{ $t('customer.moreInfo') }}</span>
                <span class="cm-card-hint d-block text-truncate">{{ moreInfoHint }}</span>
              </span>
              <i class="fas cm-muted" :class="showMore ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
            </button>
            <b-collapse v-model="showMore">
              <fieldset class="d-flex flex-column pt-2" style="gap: 8px" :disabled="!isManagerOrCounter || readOnly">
                <div class="cm-field">
                  <span class="cm-label">{{ $t('customer.reqDocuments') }}</span>
                  <multiselect
                    v-model="reqExtDocItems"
                    :options="reqExtDocList"
                    :multiple="true"
                    :placeholder="$t('customer.reqDocuments')"
                    track-by="label"
                    label="label"
                    :limit="2"
                    @input="selectReqExtDocItems"
                  ></multiselect>
                </div>
                <div class="cm-field">
                  <span class="cm-label">กลุ่มผู้ป่วย</span>
                  <PatientGroupPicker
                    v-model="customer.groupIds"
                    :clinic-url="$route.params.clinicUrl"
                    :branch-url="$route.params.branchUrl"
                    :small="true"
                    @input="groupIdsTouched = true"
                  />
                </div>
                <label class="cm-field">
                  <span class="cm-label">{{ $t('customer.additionalNotes') }}</span>
                  <b-form-textarea size="sm" v-model="customer.note" rows="1" max-rows="4"></b-form-textarea>
                </label>
                <b-form-checkbox
                  v-if="branchLinkedWithKtbPaotang"
                  v-model="customer.linkedWithKtbPaotang"
                  switch
                  style="font-weight: 400"
                >
                  {{ $t('customer.paotangLink') }}
                  <img
                    class="mr-1"
                    style="width: auto; height: 20px"
                    :src="PaotangLogo"
                  />
                </b-form-checkbox>
              </fieldset>
            </b-collapse>
          </div>
        </div>
      </div>
    </b-form>
    <template v-slot:modal-footer="{ cancel }">
      <span class="cm-legend mr-auto"><span class="text-danger">*</span> {{ $t('customer.requiredHint') }}</span>
      <b-button variant="outline-secondary" @click="cancel()">{{ $t('customer.cancel') }}</b-button>
      <b-button type="submit" form="newPatientForm" variant="primary" class="cm-save" data-testid="patient-save"
        >{{ customer.id ? $t('customer.editPatient') : $t('customer.createPatient') }}
      </b-button>
    </template>
    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import SmartCard from "@/components/SmartCard";
import SmartCardMOI from "@/components/SmartCardMOI";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog";
import UploadAvatarForm from "@/components/form/UploadAvatar";
import PatientGroupPicker from "@/components/discount/PatientGroupPicker.vue";
import MedicalChecklist from "@/components/MedicalChecklist";
import { ALLERGY_ITEMS, DISEASE_ITEMS, MEDICATION_GROUPS, MAX_LEN, NONE } from "@/utils/medicalChecklist";
import { clampThaiDigits, thaiLengthOk } from "@/utils/thaiPhoneLength";
import PaotangLogo from "../../assets/img/Paotang-small.png";
import debounce from "@/utils/debounce";
import i18n from "@/i18n";

import CountryCodeSelect from "@/components/common/CountryCodeSelect";
import { findCountryByCode } from "@/data/countryCodes";
import {
  parsePhoneNumber,
  formatPhoneForStorage,
  formatLocalInput,
  getValidationRules,
} from "@/utils/phoneUtils";

import { eventBus } from "@/main";

import {
  required,
  requiredIf,
  numeric,
  between,
  sameAs,
  not,
  helpers,
  email,
  maxLength,
} from "vuelidate/lib/validators";

const digits = number =>
  helpers.withParams(
    { type: "digits", value: number },
    value => !helpers.req(value) || helpers.len(value) == number
  );

// Thai numbers by kind (mobile 10 digits, home 9-10); other countries by their listed lengths.
const phoneLength = (kind) => (value) => {
  if (!value) return true;
  const parsed = parsePhoneNumber(value);
  const digits = parsed.nationalNumber.replace(/\D/g, "");
  if (parsed.countryCode === "TH") return thaiLengthOk(digits, kind);
  const rules = getValidationRules(parsed.countryCode);
  return digits.length >= rules.min && digits.length <= rules.max;
};

const validateThaiID = id => {
  // ตรวจสอบว่าเลขที่กรอกเข้ามามีความยาว 13 หลัก
  if (!/^\d{13}$/.test(id)) return false;

  // แปลง string ของ id ให้เป็น array ของตัวเลข
  let digits = id.split("").map(Number);

  // คำนวณผลรวมจากตัวเลข 12 ตัวแรก โดยใช้สูตร 13 - (ลำดับของตัวเลข)
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += digits[i] * (13 - i);
  }

  // คำนวณ check digit โดยเอา 11 ลบด้วยผลลัพธ์ของ sum mod 11 แล้ว mod 10 อีกครั้ง
  let checkDigit = (11 - (sum % 11)) % 10;

  // ตรวจสอบว่า check digit ตรงกับเลขตัวสุดท้ายของบัตรประชาชนหรือไม่
  return checkDigit === digits[12];
};

export default {
  name: "CustomerModal",
  i18n,
  components: {
    SmartCard,
    SmartCardMOI,
    Loading,
    Dialog,
    UploadAvatarForm,
    CountryCodeSelect,
    PatientGroupPicker,
    MedicalChecklist,
  },
  data() {
    return {
      isLoading: false,
      allergyItems: ALLERGY_ITEMS,
      diseaseItems: DISEASE_ITEMS,
      medicationGroups: MEDICATION_GROUPS,
      showMore: false,
      customer: {
        id: null,
        personalType: 1,
        personalId: null,
        dn: null,
        titleNameTh: null,
        firstNameTh: null,
        lastNameTh: null,
        titleNameEn: null,
        firstNameEn: null,
        lastNameEn: null,
        nickName: null,
        gender: null,
        mobile: null,
        email: null,
        lineId: null,
        dob: null,
        tel: null,
        congenitalDisease: null,
        allergic: null,
        medicationHistory: null,
        medicalInsuranceType: null,
        emergencyContactName: null,
        emergencyContactRelationship: null,
        emergencyContactPhone: null,
        reqDocItems: [],
        note: null,
        contentId: null,
        doctorId: null,
        address: null,
        subDistrict: null,
        district: null,
        province: null,
        postcode: null,
        country: null,
        nationality: null,
        existDn: null,
        linkedWithKtbPaotang: false,
        referralSources: [],
        groupIds: undefined,
      },
      groupIdsTouched: false,
      rawDob: null,
      reqExtDocItems: [],
      appointment: null,
      defaultContentId: null,

      isAutoAssignDn: true,
      readOnly: false,
      openFrom: null,

      dashedMobile: "",
      dashedTel: "",
      dashedEmergencyPhone: "",
      mobileCountry: "TH",
      telCountry: "TH",
      emergencyPhoneCountry: "TH",
      PaotangLogo,
      debouncedSavePatient: null,
      debouncedCheckDuplicates: null,
    };
  },
  validations() {
    if (
      (this.customer.personalId && this.customer.personalType === 1) ||
      (this.customer.linkedWithKtbPaotang && this.branchLinkedWithKtbPaotang)
    ) {
      return {
        customer: {
          personalId: { required, numeric, validateThaiID },
          firstNameTh: { required },
          lastNameTh: { required },
          allergic: { required, maxLength: maxLength(MAX_LEN) },
          congenitalDisease: { required, maxLength: maxLength(MAX_LEN) },
          medicationHistory: { required, maxLength: maxLength(MAX_LEN) },
          mobile: {
            required,
            validPhone: phoneLength("mobile"),
          },
          tel: { validPhone: phoneLength("home") },
        },
      };
    } else {
      return {
        customer: {
          personalId: {},
          firstNameTh: { required },
          lastNameTh: { required },
          allergic: { required, maxLength: maxLength(MAX_LEN) },
          congenitalDisease: { required, maxLength: maxLength(MAX_LEN) },
          medicationHistory: { required, maxLength: maxLength(MAX_LEN) },
          mobile: {
            required,
            validPhone: phoneLength("mobile"),
          },
          tel: { validPhone: phoneLength("home") },
        },
      };
    }
  },
  created() {
    this.init();
  },
  mounted() {
    this.debouncedSavePatient = debounce(this.savePatient);
    this.debouncedCheckDuplicates = debounce(this.checkDupicates);
  },
  computed: {
    ...mapGetters({
      getUserInfo: "moduleUser/getUserInfo",
      getGenderList: "moduleMaster/getGenderList",
      getTitleNameList: "moduleMaster/getTitleNameList",
      getTitleNameEngList: "moduleMaster/getTitleNameEngList",
      personalTypeList: "moduleMaster/getPersonalTypeList",
      reqExtDocList: "moduleMaster/getReqExtDocList",
      reqExtDocBy: "moduleMaster/getReqExtDocBy",
      doctorList: "moduleUser/getDoctorList",
      appointmentBy: "moduleAppointment/getQueueAppointmentBy",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      getBranchInfo: "moduleUser/getBranchInfo",
      branchLinkedWithKtbPaotang: "moduleUser/getLinkedWithKtbPaotang",
      getGroupList: "modulePatientGroup/getGroupList",
    }),
    patientGroupOptions() {
      return (this.getGroupList || []).map(g => ({
        text: g.name,
        value: g.id,
      }));
    },
    now() {
      return moment().format();
    },
    isManagerOrCounter() {
      return this.isAccessMenu(["COUNTER", "MANAGER", "HEADCOUNTER"]);
    },
    currentLang() {
      return this.$i18n.locale;
    },
    referralChipOptions() {
      return this.sourceOptions.filter((o) => o.value);
    },
    moreInfoHint() {
      const hint = this.$t("customer.moreInfoHint");
      return this.branchLinkedWithKtbPaotang ? `${hint} · ${this.$t("customer.paotang")}` : hint;
    },
    allergyAlert() {
      const text = (this.customer.allergic || "").trim();
      return text && text !== NONE ? text : "";
    },
    sourceOptions() {
      return [
        { value: null, text: this.$t('customer.referralSource'), disabled: true },
        { value: "facebook_ad", text: this.$t('customer.referralSources.facebook_ad') },
        { value: "google_ad", text: this.$t('customer.referralSources.google_ad') },
        { value: "instagram_ad", text: this.$t('customer.referralSources.instagram_ad') },
        { value: "tiktok_ad", text: this.$t('customer.referralSources.tiktok_ad') },
        { value: "youtube", text: this.$t('customer.referralSources.youtube') },
        { value: "website", text: this.$t('customer.referralSources.website') },
        { value: "friend_referral", text: this.$t('customer.referralSources.friend_referral') },
        { value: "doctor_referral", text: this.$t('customer.referralSources.doctor_referral') },
        { value: "walk_in", text: this.$t('customer.referralSources.walk_in') },
        { value: "printed_media", text: this.$t('customer.referralSources.printed_media') },
        { value: "event_expo", text: this.$t('customer.referralSources.event_expo') },
        { value: "other", text: this.$t('customer.referralSources.other') },
      ];
    },
    translatedRelationshipOptions() {
      return [
        this.$t('customer.relationships.father'),
        this.$t('customer.relationships.mother'),
        this.$t('customer.relationships.older_brother'),
        this.$t('customer.relationships.older_sister'),
        this.$t('customer.relationships.younger_brother'),
        this.$t('customer.relationships.younger_sister'),
        this.$t('customer.relationships.spouse'),
        this.$t('customer.relationships.husband'),
        this.$t('customer.relationships.wife'),
        this.$t('customer.relationships.child'),
        this.$t('customer.relationships.son'),
        this.$t('customer.relationships.daughter'),
        this.$t('customer.relationships.friend'),
        this.$t('customer.relationships.relative'),
        this.$t('customer.relationships.other'),
      ];
    },
    formattedDob() {
      if (this.rawDob) {
        const d = moment(this.rawDob).locale("th").format("YYYY-MM-DD");
        return d === "Invalid date" ? null : d;
      }
      return null;
    },
  },
  methods: {
    ...mapActions({
      createPatient: "modulePatient/createPatient",
      updatePatient: "modulePatient/updatePatient",
      updateStateQueueAppointmentPatient:
        "moduleAppointment/updateStateQueueAppointmentPatient",
      checkNamePatient: "modulePatient/checkNamePatient",
      checkDuplicate: "modulePatient/checkDuplicate",
      fetchGroupList: "modulePatientGroup/fetchGroupList",
    }),
    init() {
      const clinicUrl = this.$route?.params?.clinicUrl;
      const branchUrl = this.$route?.params?.branchUrl;
      if (clinicUrl && branchUrl) {
        this.fetchGroupList({ clinicUrl, branchUrl });
      }
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('customerFormLang', lang);
    },
    show(customer, appointment = null, readOnly = false, openFrom = null) {
      this.setDefultCustomer();
      this.appointment = appointment; //ห้ามแก้เป็น {...appointment} จะทำให้เกิด bug ตอนเปลี่ยนจาก unNameApt เป็น apt Why???
      this.readOnly = readOnly;
      this.openFrom = openFrom;

      this.$refs["CustomerModal"].show();
      if (customer) {
        // groupIds must be in the Object.assign payload so Vue 2 makes it reactive;
        // assigning it after replaces a non-reactive property and the picker won't sync.
        const groupIds = Array.isArray(customer.groupMembers) && customer.groupMembers.length > 0
          ? customer.groupMembers.map(m => m.groupId)
          : [];
        this.customer = Object.assign({}, customer, {
          referralSources: Array.isArray(customer.referralSources) ? customer.referralSources : [],
          groupIds,
        });
        this.groupIdsTouched = false;
        this.rawDob = this.customer.dob;
        // Parse phone numbers and set country dropdowns
        if (customer.mobile) {
          const parsed = parsePhoneNumber(customer.mobile);
          this.mobileCountry = parsed.countryCode;
          this.customer.mobile = formatPhoneForStorage(
            parsed.dialCode,
            parsed.nationalNumber
          );
          this.dashedMobile = formatLocalInput(
            parsed.nationalNumber,
            parsed.countryCode
          );
        }
        if (customer.tel) {
          const parsed = parsePhoneNumber(customer.tel);
          this.telCountry = parsed.countryCode;
          this.customer.tel = formatPhoneForStorage(
            parsed.dialCode,
            parsed.nationalNumber
          );
          this.dashedTel = formatLocalInput(
            parsed.nationalNumber,
            parsed.countryCode
          );
        }
        if (customer.emergencyContactPhone) {
          const parsed = parsePhoneNumber(customer.emergencyContactPhone);
          this.emergencyPhoneCountry = parsed.countryCode;
          this.customer.emergencyContactPhone = formatPhoneForStorage(
            parsed.dialCode,
            parsed.nationalNumber
          );
          this.dashedEmergencyPhone = formatLocalInput(
            parsed.nationalNumber,
            parsed.countryCode
          );
        }
        this.reqExtDocItems = [];
        this.customer.reqDocItems?.forEach(item => {
          const reqExtDoc = this.reqExtDocBy(item, "val");
          this.reqExtDocItems.push(reqExtDoc);
        });
        this.defaultContentId = customer.contentId ? customer.contentId : null;
        this.showMore = Boolean(
          (customer.note && customer.note.trim()) ||
            groupIds.length ||
            (customer.reqDocItems && customer.reqDocItems.length) ||
            customer.linkedWithKtbPaotang
        );
      } else {
        // this.setDefultCustomer();
      }
    },
    close() {
      this.setDefultCustomer();
      this.$v.$reset();
    },
    getReaderData(readerData) {
      if (readerData) {
        this.customer.personalType = 1;
        this.customer.personalId = readerData.Citizenid;
        this.customer.titleNameTh = readerData.Th_Prefix;
        this.customer.firstNameTh = readerData.Th_Firstname;
        this.customer.lastNameTh = readerData.Th_Lastname;
        this.customer.titleNameEn = readerData.En_Prefix;
        this.customer.firstNameEn = readerData.En_Firstname;
        this.customer.lastNameEn = readerData.En_Lastname;
        this.customer.gender = readerData.gender;
        this.rawDob = readerData.Birthday;
        this.customer.province = readerData.addrProvince;
        this.customer.district = readerData.addrAmphur;
        this.customer.subDistrict = readerData.addrTambol;
        this.customer.address = readerData.addrHouseNo;
        this.photoRawToFile(readerData.PhotoRaw);
      }
    },
    photoRawToFile(data) {
      var dataURI = "data:image/jpeg;base64," + data;
      this.$refs.UploadAvatar.setImage(
        dataURI,
        `cid-${this.customer.personalId}.jpg`
      );
    },
    uploadAvatarCallback(state, contentId) {
      switch (state) {
        case "uploading":
          this.isLoading = true;
          break;
        case "callback":
          this.isLoading = false;
          this.checkPatient();
          break;
        case "delete":
          this.customer.contentId = contentId;
          break;
        case "reset":
          this.customer.contentId = contentId;
          break;
        case "success":
          this.isLoading = false;
          this.customer.contentId = contentId;
          this.checkPatient();
          break;
        case "fail":
          this.isLoading = false;
          this.showDialogToast("danger", this.$t('customer.messages.uploadFailed'));
          this.checkPatient();
          break;
        default:
      }
    },
    resetForm() {
      this.$refs.UploadAvatar.reset("");
      this.setDefultCustomer();
      this.$v.$reset();
    },
    async savePatient() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        await this.$nextTick();
        const bad = this.$refs.newPatientForm.$el.querySelector(".is-invalid");
        if (bad) {
          bad.scrollIntoView({ block: "center", behavior: "smooth" });
          const field = bad.matches("input, textarea") ? bad : bad.querySelector("button:not([disabled])");
          if (field) field.focus({ preventScroll: true });
        }
        this.$refs.Dialog.showToast("warning", this.$t("customer.validation.fixHighlighted"));
        return;
      }
      this.isLoading = true;
      this.$refs.UploadAvatar.submit();
    },
    async checkPatient() {
      let isDismissed;
      if (
        this.getBranchInfo.linkedWithNhso &&
        this.customer.personalType === 1 &&
        this.customer.personalId?.length !== 13
      ) {
        await this.$refs.Dialog.showAlertConfirm(
          "คุณไม่ได้กรอกเลขบัตรประชาชน",
          "ต้องการสร้างคนไข้ใหม่ โดยไม่มีเลขบัตรประชาชน",
          "warning",
          "สร้างคนไข้ใหม่โดยไม่มีเลขบัตร",
          "กลับไปเพิ่มเลขบัตร"
        ).then(res => {
          isDismissed = res.isDismissed;
        });
      }
      if (isDismissed) return;

      this.isLoading = true;
      let matched = await this.checkDupicatePatients();
      this.isLoading = false;

      if (matched && matched.duplicateBy === "PID") {
        this.$refs.Dialog.showAlertInfo(
          "เลขประจำตัวซ้ำ กับคนไข้เดิม",
          "เลือกจากคนไข้เดิม",
          "warning"
        ).then(() => {
          this.openSearchCustomerModal(this.customer.personalId);
          this.isLoading = false;
          return;
        });
      } else if (
        matched &&
        matched.duplicateBy === "NAME" &&
        !this.customer.id
      ) {
        this.$refs.Dialog.showAlertConfirm(
          "ชื่อ และนามสกุลซ้ำกับคนไข้ที่เดิม",
          "ต้องการสร้างคนไข้ซ้ำ?",
          "warning",
          "สร้างคนไข้ชื่อซ้ำ",
          "เลือกจากคนไข้เดิม",
          true
        ).then(res => {
          if (res.isDismissed) {
            this.openSearchCustomerModal(
              this.customer.firstNameTh + " " + this.customer.lastNameTh
            );
            this.isLoading = false;
            return;
          } else {
            this.saveCustomer();
          }
        });
      } else {
        this.saveCustomer();
      }
    },
    buildCustomerPayload() {
      const payload = {
        ...this.customer,
        dob: this.formattedDob,
      };
      // Only send groupIds if user touched the group picker
      if (!this.groupIdsTouched) {
        delete payload.groupIds;
      } else {
        payload.groupIds = payload.groupIds || [];
      }
      return payload;
    },
    saveCustomer() {
      this.isLoading = true;

      if (!this.customer.id) {
        this.createPatient({
          customer: this.buildCustomerPayload(),
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        })
          .then(res => {
            this.isLoading = false;
            if (res.status == 200) {
              this.customer.dn = res.data.dn;
              this.customer.id = res.data.id;

              if (this.openFrom === "UnnamedPtApt") {
                eventBus.$emit("applyPt", this.customer);
                eventBus.$emit("alertToast", {
                  message: "สร้างคนไข้สำเร็จ",
                  variant: "success",
                });
              } else if (this.openFrom === "QManage") {
                let event = {
                  id: parseInt(this.appointment.id),
                  startDt: this.appointment.start,
                  endDt: this.appointment.end,
                  roomId: parseInt(this.appointment.extendedProps.roomId),
                  aptTreatItems: this.appointment.extendedProps.treatmentList,
                  rmdId: this.appointment.extendedProps.rmdId,
                  doctorUid: this.appointment.extendedProps.doctor,
                  note: this.appointment.extendedProps.note,
                  patientId: this.customer.id,
                };
                this.$store.dispatch(
                  "moduleAppointment/updateQueueAppointment",
                  {
                    event,
                    clinicUrl: this.$route.params.clinicUrl,
                    branchUrl: this.$route.params.branchUrl,
                  }
                );
              } else {
                this.$emit(
                  "openProfileCustomerModal",
                  Object.assign({}, this.appointment),
                  Object.assign({}, this.customer)
                );
              }

              this.$refs["CustomerModal"].hide();
            }
          })
          .catch(res => {
            this.isLoading = false;
            if (res.response?.data?.code === "PAT0001") {
              this.$root.$emit("bv::show::tooltip", "personalId");
              this.$refs.Dialog.showAlertInfo(
                "เลขบัตรประชาชน/Passport ซ้ำ",
                "ไม่สามารถสร้างประวัติคนไข้ใหม่ได้",
                "warning"
              ).then(() => {
                setTimeout(
                  () => this.$root.$emit("bv::hide::tooltip", "personalId"),
                  8000
                );
              });
            }
          });
      } else {
        this.$refs.Dialog.showAlertConfirm(
          "แก้ไขข้อมูลคนไข้ ?",
          "คุณต้องการแก้ไข รายละเอียดข้อมูลคนไข้"
        ).then(res => {
          if (res.isConfirmed) {
            this.updatePatient({
              customer: this.buildCustomerPayload(),
              clinicUrl: this.$route.params.clinicUrl,
              branchUrl: this.$route.params.branchUrl,
            })
              .then(res => {
                if (res.status == "200") {
                  this.$refs["CustomerModal"].hide();
                  this.customer = res.data;
                  let appointment = Object.assign({}, this.appointment);
                  let patient = Object.assign({}, this.customer);
                  if (appointment && patient) {
                    this.updateStateQueueAppointmentPatient({
                      id: appointment.id,
                      patient: patient,
                    });
                  }
                  eventBus.$emit("updatePatientInfo");

                  // this.$emit("openProfileCustomerModal", appointment, patient);
                }
                this.isLoading = false;
              })
              .catch(err => {
                console.error(err);
                this.isLoading = false;
              });
          } else {
            this.isLoading = false;
          }
        });
      }
    },
    async checkDupicates() {
      let matched = await this.checkDupicatePatients();
      if (matched && matched.duplicateBy === "PID") {
        this.$root.$emit("bv::show::popover", "matched-pid-popover");
      } else if (matched && matched.duplicateBy === "NAME") {
        this.$root.$emit("bv::show::popover", "matched-name-popover");
      }
    },
    checkDupicatePatients() {
      let { personalId, firstNameTh, lastNameTh } = this.customer;
      if (!personalId && !firstNameTh && !lastNameTh) return;
      if (this.customer.personalId === "") {
        this.customer.personalId = null;
      }
      if (this.customer.firstNameTh === "") {
        this.customer.firstNameTh = null;
      }
      if (this.customer.lastNameTh === "") {
        this.customer.lastNameTh = null;
      }
      return this.checkDuplicate({
        customer: this.customer,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      }).then(res => {
        let dupData = res.data;
        if (dupData?.result?.length > 0) {
          return res.data;
        } else {
          this.closePopover();
          return false;
        }
      });
    },
    closePopover() {
      this.$root.$emit("bv::hide::popover");
    },
    customLabel(option) {
      return `${option.label}`;
    },
    selectReqExtDocItems() {
      let reqExtDocItems = [];

      this.reqExtDocItems.forEach(element => {
        reqExtDocItems.push(element.val);
      });
      this.customer.reqDocItems = reqExtDocItems;
    },
    resetButton() {
      this.$refs.Dialog.showAlertConfirm("รีเซ็ตข้อมูล ?").then(result => {
        if (!result.value) {
          return;
        }
      });
      this.setDefultCustomer();
    },
    setDefultCustomer() {
      this.showMore = false;
      this.customer = Object.assign(
        {},
        {
          personalType: 1,
          personalId: null,
          dn: null,
          titleNameTh: null,
          firstNameTh: null,
          lastNameTh: null,
          titleNameEn: null,
          firstNameEn: null,
          lastNameEn: null,
          nickName: null,
          gender: null,
          mobile: null,
          email: null,
          lineId: null,
          dob: null,
          tel: null,
          congenitalDisease: null,
          allergic: null,
          medicationHistory: null,
          medicalInsuranceType: null,
          emergencyContactName: null,
          emergencyContactRelationship: null,
          emergencyContactPhone: null,
          reqDocItems: [],
          note: null,
          contentId: null,
          doctorId: null,
          address: null,
          subDistrict: null,
          district: null,
          province: null,
          postcode: null,
          country: null,
          nationality: null,
          existDn: null,
          linkedWithKtbPaotang: false,
          referralSources: [],
          groupIds: undefined,
        }
      );
      this.groupIdsTouched = false;
      this.isLoading = false;
      this.reqExtDocItems = [];
      this.appointment = null;
      this.defaultContentId = null;
      this.dashedMobile = "";
      this.dashedTel = "";
      this.dashedEmergencyPhone = "";
      this.mobileCountry = "TH";
      this.telCountry = "TH";
      this.emergencyPhoneCountry = "TH";
      this.rawDob = null;
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    openSearchCustomerModal(val) {
      this.$refs["CustomerModal"].hide();
      this.$parent.openSearchCustomerModal(val);
    },
    // Typed phone → [stored "+66812345678", shown "081-234-5678"]. Thai numbers stop at 10
    // digits; an emptied field (or a lone 0) stores nothing rather than a bare dial code.
    phoneInput(raw, countryCode) {
      let num = (raw || "").replace(/[^0-9]/g, "");
      if (countryCode === "TH") num = clampThaiDigits(num);
      const { dialCode } = findCountryByCode(countryCode);
      const stored = formatPhoneForStorage(dialCode, num);
      return [stored === dialCode ? "" : stored, formatLocalInput(num, countryCode)];
    },
    applyMobileNumber(e) {
      [this.$v.customer.mobile.$model, this.dashedMobile] = this.phoneInput(e, this.mobileCountry);
    },
    applyTelNumber(e) {
      [this.$v.customer.tel.$model, this.dashedTel] = this.phoneInput(e, this.telCountry);
    },
    applyEmergencyPhone(e) {
      [this.customer.emergencyContactPhone, this.dashedEmergencyPhone] = this.phoneInput(
        e,
        this.emergencyPhoneCountry
      );
    },
    // "081-234-5678" is 12 characters; other countries aren't capped while typing
    phoneMaxLength(countryCode) {
      return countryCode === "TH" ? 12 : null;
    },
    phoneLengthHint(countryCode, kind) {
      if (countryCode !== "TH") return this.$t("customer.validation.phoneDigits");
      return this.$t(kind === "mobile" ? "customer.validation.mobileDigits" : "customer.validation.homeDigits");
    },
    onMobileCountryChange(country) {
      if (this.customer.mobile) {
        const parsed = parsePhoneNumber(this.customer.mobile);
        this.customer.mobile = formatPhoneForStorage(country.dialCode, parsed.nationalNumber);
        this.dashedMobile = formatLocalInput(parsed.nationalNumber, country.code);
      }
    },
    onTelCountryChange(country) {
      if (this.customer.tel) {
        const parsed = parsePhoneNumber(this.customer.tel);
        this.customer.tel = formatPhoneForStorage(country.dialCode, parsed.nationalNumber);
        this.dashedTel = formatLocalInput(parsed.nationalNumber, country.code);
      }
    },
    onEmergencyCountryChange(country) {
      if (this.customer.emergencyContactPhone) {
        const parsed = parsePhoneNumber(this.customer.emergencyContactPhone);
        this.customer.emergencyContactPhone = formatPhoneForStorage(country.dialCode, parsed.nationalNumber);
        this.dashedEmergencyPhone = formatLocalInput(parsed.nationalNumber, country.code);
      }
    },
  },
};
</script>

<style>
/* header, body and footer belong to b-modal, so they can't be reached from the scoped block */
.modal .modal-dialog.modal-xl.fluid.cm-dialog {
  max-width: min(1280px, 96vw);
  padding-left: 0;
  padding-right: 0;
}
.cm-dialog .modal-header {
  align-items: center;
  padding: 12px 20px;
  background: #1554a0;
  border-bottom: 0;
  color: #fff;
}
.cm-dialog .modal-body {
  background: #eef2f7;
  padding: 16px 20px;
}
.cm-dialog .modal-footer {
  padding: 12px 20px;
  border-top-color: #e4e9f0;
}
.cm-dialog .cm-legend {
  color: #475467;
  font-size: 0.8125rem;
}
.cm-dialog .cm-save {
  min-width: 140px;
  background: #1554a0;
  border-color: #1554a0;
  font-weight: 600;
}
.cm-dialog .cm-save:hover,
.cm-dialog .cm-save:focus {
  background: #0f3d6e;
  border-color: #0f3d6e;
}
</style>

<style scoped>
.cm-dn {
  padding: 2px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 0.8125rem;
  align-self: center;
}
.cm-allergy {
  max-width: 40%;
  align-self: center;
  padding: 3px 12px;
  border-radius: 14px;
  background: #fff1f0;
  color: #b42318;
  font-size: 0.8125rem;
}
.cm-lang {
  display: inline-flex;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 7px;
  overflow: hidden;
}
.cm-lang button {
  padding: 3px 12px;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 0.75rem;
}
.cm-lang button.on {
  background: #fff;
  color: #1554a0;
  font-weight: 600;
}
.cm-close {
  width: 32px;
  height: 32px;
  margin-left: 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #fff;
}
.cm-close:hover {
  background: rgba(255, 255, 255, 0.15);
}
.cm-cols {
  display: grid;
  grid-template-columns: 380px minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;
}
@media (max-width: 1199.98px) {
  .cm-cols {
    grid-template-columns: minmax(0, 1fr);
  }
}
.cm-col {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}
/* soaks up the leftover height so all three columns end on the same line */
.cm-grow {
  flex-grow: 1;
}
.cm-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid #e4e9f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  min-width: 0;
}
.cm-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cm-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-size: 0.8125rem;
}
.cm-tile-blue { background: #e3edfb; color: #1554a0; }
.cm-tile-red { background: #fde8e7; color: #b42318; }
.cm-tile-green { background: #dcfae6; color: #067647; }
.cm-tile-orange { background: #ffead5; color: #b93815; }
.cm-tile-violet { background: #ebe9fe; color: #5925dc; }
.cm-tile-teal { background: #d1f1f6; color: #0e6779; }
.cm-tile-gray { background: #f2f4f7; color: #475467; }
.cm-tile-pink { background: #fce7f6; color: #c11574; }
.cm-card-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #101828;
  line-height: 1.25;
}
.cm-card-hint {
  font-size: 0.75rem;
  color: #667085;
  line-height: 1.3;
}
.cm-grid {
  display: grid;
  gap: 8px;
}
.cm-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  min-width: 0;
}
.cm-label {
  font-size: 0.75rem;
  color: #475467;
}
.cm-muted {
  color: #475467;
}
/* required fields keep the original form's blue tint */
.cm-req:not(.is-invalid) {
  border-color: #b9cbe6;
  background: #f7faff;
}
.cm-reader {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
/* SmartCard / SmartCardMOI render a block b-button; shrink both to header buttons */
.cm-reader ::v-deep .btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: auto;
  height: 28px;
  margin: 0;
  padding: 0 10px;
  border: 1px solid #b9cbe6;
  border-radius: 7px;
  background: #f5f8fd;
  color: #1554a0;
  font-size: 0.75rem;
  line-height: 1.2 !important;
}
.cm-reader ::v-deep .btn:hover {
  background: #e3edfb;
}
.cm-reader ::v-deep .btn::before {
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  content: "\f2c2";
}
.cm-reader ::v-deep .small-text {
  font-size: inherit;
}
.cm-photo ::v-deep .avatar-container {
  position: relative;
}
.cm-photo ::v-deep .upload-btn-wrapper {
  border: 1px solid #e4e9f0;
  border-radius: 12px;
}
.cm-photo ::v-deep .b-avatar {
  border-radius: 12px !important;
  background: #f2f5f9;
  color: #b4c0d0;
}
.cm-photo-menu {
  position: absolute;
  right: -8px;
  bottom: -8px;
}
.cm-photo-menu ::v-deep .cm-photo-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #1554a0;
  color: #fff;
  font-size: 0.75rem;
}
.cm-photo-menu ::v-deep .cm-photo-badge:hover {
  background: #0f3d6e;
}
.cm-more-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #344054;
  text-align: left;
  min-width: 0;
}
.cm-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.cm-chips ::v-deep .btn {
  flex: 0 0 auto;
  margin: 0;
  padding: 3px 12px;
  border-radius: 999px !important;
  font-size: 0.8125rem;
}
.required {
  background-color: #ccecff6c;
}

.form-row {
  margin-left: 0;
  margin-right: 0;
}

/* Make all form controls smaller - reduce height, padding, line-height */
.form-control-sm,
.form-control.form-control-sm {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Target regular form-control inputs with size="sm" */
.form-control[size="sm"] {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Make textareas smaller */
textarea.form-control-sm {
  min-height: auto !important;
  padding: 0.25rem 0.5rem !important;
}

/* Make select dropdowns smaller */
select.form-control-sm {
  height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
}

/* Make datetime picker input smaller */
::v-deep .vdatetime-input {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

::v-deep .vdatetime input.form-control {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

::v-deep .vdatetime .form-control {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Make vue-typeahead-bootstrap smaller */
::v-deep .vue-typeahead-bootstrap input.form-control-sm {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

::v-deep .vue-typeahead-bootstrap .dropdown-item {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
}

/* Make multiselect component smaller */
::v-deep .multiselect {
  min-height: 31px !important;
  font-size: 0.875rem !important;
}

::v-deep .multiselect__tags {
  min-height: 31px !important;
  padding: 3px 40px 0 4px !important;
  font-size: 0.875rem !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  font-size: 0.875rem !important;
  padding: 0 0 0 3px !important;
  margin-bottom: 3px !important;
  min-height: 20px !important;
  line-height: 20px !important;
}

::v-deep .multiselect__select {
  height: 31px !important;
}

::v-deep .multiselect__option {
  padding: 6px 12px !important;
  min-height: 28px !important;
  font-size: 0.875rem !important;
  line-height: 1.3 !important;
}

::v-deep .multiselect__tag {
  padding: 2px 22px 2px 6px !important;
  margin-bottom: 2px !important;
  font-size: 0.75rem !important;
  line-height: 1.3 !important;
}

::v-deep .multiselect__tag-icon {
  line-height: 18px !important;
  width: 18px !important;
}

::v-deep .multiselect__tag-icon:after {
  line-height: 18px !important;
}

::v-deep .multiselect__placeholder {
  font-size: 0.875rem !important;
  padding-top: 2px !important;
  margin-bottom: 3px !important;
}

/* Phone input next to country code dropdown - remove left border-radius */
.country-code-select + .form-control,
.country-code-select + .form-control-sm {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>